import Long from 'long';
import {action, computed, makeObservable, observable, toJS} from 'mobx';

import {WorkspaceStore} from '.';
import {entities} from '../../api/proto';
import {equalUint8Arrays} from '../../utils/arrayUtils';
import {IRawChat} from '../RawChatsStore/RawChat';
import WorkspaceMember from './WorkspaceMember';
import {WorkspaceMemberAccessRecord} from './WorkspaceMemberAccessRecord';

export interface IWorkspaceMemberConfig {
  accessToContactInfo: boolean;
  accessToContactsList: boolean;
  allowManageChats: boolean;
}

export const createDefaultWorkspaceMember = (workspace: WorkspaceStore) => {
  return new WorkspaceMember(
    {
      access: {
        records: workspace.availableChannels.map((channel) => ({
          channelID: channel.id,
          disableChannel: false,
          chatsAllowList: [],
        })),
      },
    },
    workspace,
  );
};

export class WorkspaceMemberConfig implements IWorkspaceMemberConfig {
  @observable permissions: entities.WorkspaceMemberPermission[] = [];
  @observable channelAccessRecords: WorkspaceMemberAccessRecord[] = [];

  @computed get rawChannelAccessRecords() {
    return this.channelAccessRecords.map(({channelID, chatsAllowList, chatsDenyList, disableChannel}) => ({
      channelID,
      chatsAllowList: toJS(chatsAllowList) || [],
      chatsDenyList: toJS(chatsDenyList) || [],
      disableChannel: disableChannel,
    }));
  }

  constructor(
    public workspace: WorkspaceStore,
    public member: WorkspaceMember = createDefaultWorkspaceMember(workspace),
  ) {
    makeObservable(this);
    this.assign_(this.member);

    this.initChannelAccessRecords(member);
    this.initPermitions(member);
  }

  copy = () => {
    return new WorkspaceMemberConfig(this.workspace, this.member);
  };

  @action protected assign_ = (member?: WorkspaceMember | null) => {
    this.permissions = member?.permissions || [];
  };

  @action initChannelAccessRecords = (member: WorkspaceMember) => {
    const records_ =
      member?.access?.records?.map((record) => new WorkspaceMemberAccessRecord(record, this.workspace)) || [];

    this.channelAccessRecords = records_.sort((a, b) => Number(!!a.disableChannel) - Number(!!b.disableChannel));
  };

  @action initPermitions = (member: WorkspaceMember) => {
    const opt = member.opts?.find((opt) => opt.type === entities.WorkspaceMemberOptType.WMOT_SHOW_CONTACT_INFO);

    const show = opt ? (opt.value?.boolValue ?? false) : this.workspace.app.features.showContactInfo;

    this.showContactInformation = show;
  };

  @action fill = (props?: WorkspaceMemberConfig) => {
    if (props) {
      this.member.assign({
        permissions: props.permissions,
        access: {
          records: props.rawChannelAccessRecords,
        },
      });
      this.permissions = props.permissions;
      this.channelAccessRecords = props.channelAccessRecords;
    }
  };

  // Member Permission //
  @observable showContactInformation: boolean = true;

  @observable accessToNewChats: boolean = true;
  @observable accessToContactInfo: boolean = false;
  @observable accessToContactsList: boolean = false;
  @observable allowManageChats: boolean = false;

  @action toggleAccessToPersonalInfo = () => {
    this.showContactInformation = !this.showContactInformation;
  };

  @action toggleAccessToContactInfo = () => {
    this.accessToContactInfo = !this.accessToContactInfo;
  };

  @action toggleAccessToContactsList = () => {
    this.accessToContactsList = !this.accessToContactsList;
  };

  @action toggleAllowManageChats = () => {
    this.allowManageChats = !this.allowManageChats;
  };

  @action enableAllPermissions = () => {
    this.showContactInformation = true;

    this.accessToContactInfo = false;
    this.accessToContactsList = false;
    this.allowManageChats = false;
  };

  @action allowAllChannels = () => {
    this.channelAccessRecords.forEach((record) => {
      record.reset();
    });
  };

  hasAccessToChannel = (channelID: Uint8Array): boolean => {
    return !this.channelAccessRecords.find((r) => equalUint8Arrays(r.channelID, channelID))?.disableChannel;
  };

  hasAccessToChat = (channelID: Uint8Array, chatID: Long): boolean => {
    const accessRecord = this.channelAccessRecords.find((r) => equalUint8Arrays(r.channelID, channelID));
    return !!accessRecord?.hasAccessToChat(chatID);
  };

  addAccessToChat = (chat: IRawChat) => {
    const accessRecord = this.channelAccessRecords.find((r) => equalUint8Arrays(r.channelID, chat.channelID));
    accessRecord?.addAccessToChat(chat);
  };

  removeAccessToChat = (chat: IRawChat) => {
    const accessRecord = this.channelAccessRecords.find((r) => equalUint8Arrays(r.channelID, chat.channelID));
    accessRecord?.removeAccessToChat(chat);
  };
}

export default WorkspaceMemberConfig;
